import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Lock, Mail, ArrowRight, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';


export function Auth() {
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);



  useEffect(() => {
    // Pre-fill email from URL if present (manual login flow)
    const params = new URLSearchParams(window.location.search);
    const loginEmail = params.get('login_email');
    if (loginEmail) setEmail(loginEmail.trim().toLowerCase());
  }, []);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setMessage({ text: "Veuillez saisir une adresse email valide.", type: 'error' });
      return;
    }

    setLoading(true);
    setMessage(null);

    const cleanEmail = email.toLowerCase().trim();

    try {
      // 1. Validate if the email exists in any allowed role
      const isSuperAdmin = cleanEmail === 'benmloukaamen@gmail.com';
      
      const { data: orgAdmins } = await supabase
        .from('organization_admins')
        .select('id')
        .eq('email', cleanEmail)
        .eq('email_confirmed', true)
        .is('deleted_at', null)
        .limit(1);
      const isOrgAdmin = orgAdmins && orgAdmins.length > 0;

      const { data: catManagers } = await supabase
        .from('category_managers')
        .select('id')
        .eq('email', cleanEmail)
        .eq('email_confirmed', true)
        .is('deleted_at', null)
        .limit(1);
      const isManager = catManagers && catManagers.length > 0;

      let isTeam = false;
      try {
        const { data: teamRecs } = await supabase.rpc('resolve_staff_by_email', { p_email: cleanEmail });
        if (teamRecs && teamRecs.length > 0 && teamRecs[0].email_confirmed) {
          isTeam = true;
        }
      } catch (err) {
        console.error("Error resolving team by email via RPC:", err);
      }

      if (!isSuperAdmin && !isOrgAdmin && !isManager && !isTeam) {
        setMessage({ 
          text: "Cette adresse email n'est pas autorisée ou n'a pas encore été confirmée. Veuillez contacter votre administrateur.", 
          type: 'error' 
        });
        setLoading(false);
        return;
      }

      // 2. Generate secure 6-digit OTP
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      console.log(`🔑 [DEBUG OTP] Code de validation pour ${cleanEmail} : ${code}`);

      // 3. Write OTP to Database (Trigger will automatically send the Resend email!)
      const { error: dbError } = await supabase
        .from('login_otps')
        .insert({
          email: cleanEmail,
          code: code,
          expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString() // 15 mins validity
        });

      if (dbError) throw dbError;

      setCodeSent(true);
      setMessage({ text: `Code de connexion envoyé à ${cleanEmail} !`, type: 'success' });
    } catch (err: any) {
      console.error("Error requesting login OTP:", err);
      setMessage({ text: `Erreur d'envoi : ${err.message || 'Service indisponible'}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (codeInput.length !== 6) {
      setMessage({ text: "Le code doit comporter 6 chiffres.", type: 'error' });
      return;
    }

    setLoading(true);
    setMessage(null);

    const cleanEmail = email.toLowerCase().trim();

    try {
      // Verify OTP in DB
      const { data: otpRecord, error: otpError } = await supabase
        .from('login_otps')
        .select('*')
        .eq('email', cleanEmail)
        .eq('code', codeInput)
        .gt('expires_at', new Date().toISOString())
        .is('used_at', null)
        .maybeSingle();

      if (otpError) throw otpError;

      if (!otpRecord) {
        setMessage({ text: "Code incorrect, expiré ou déjà utilisé.", type: 'error' });
        setLoading(false);
        return;
      }

      // Mark OTP as used
      await supabase
        .from('login_otps')
        .update({ used_at: new Date().toISOString() })
        .eq('id', otpRecord.id);

      // Determine user credentials and set corresponding tokens
      localStorage.removeItem('wfm_staff_token');
      localStorage.removeItem('wfm_manager_token');
      localStorage.removeItem('wfm_org_token');
      localStorage.removeItem('wfm_user_email');

      localStorage.setItem('wfm_user_email', cleanEmail);
      
      let userRole = 'staff';
      let teamId = null;
      let orgId = null;

      // A. Check Org Admin token from DB
      const { data: dbAdmins } = await supabase
        .from('organization_admins')
        .select('token, org_id')
        .eq('email', cleanEmail)
        .eq('email_confirmed', true)
        .is('deleted_at', null)
        .limit(1);
      
      if (dbAdmins && dbAdmins.length > 0) {
        localStorage.setItem('wfm_org_token', dbAdmins[0].token);
        userRole = 'admin';
        orgId = dbAdmins[0].org_id;
      } else {
        // B. Check Manager token from DB
        const { data: dbManagers } = await supabase
          .from('category_managers')
          .select('token, category_id')
          .eq('email', cleanEmail)
          .eq('email_confirmed', true)
          .is('deleted_at', null)
          .limit(1);

        if (dbManagers && dbManagers.length > 0) {
          localStorage.setItem('wfm_manager_token', dbManagers[0].token);
          userRole = 'manager';
          
          // Get orgId for the manager's category securely via RPC
          const { data: managerRecs } = await supabase
            .rpc('resolve_manager_token', { p_token: dbManagers[0].token });
          if (managerRecs && managerRecs.length > 0) {
            orgId = managerRecs[0].org_id;
          } else {
            orgId = null;
          }
        } else {
          // C. Check Team token via RPC to bypass RLS bootstrap deadlock
          try {
            const { data: teamRecs } = await supabase.rpc('resolve_staff_by_email', { p_email: cleanEmail });
            if (teamRecs && teamRecs.length > 0 && teamRecs[0].email_confirmed && teamRecs[0].staff_token) {
              localStorage.setItem('wfm_staff_token', teamRecs[0].staff_token);
              userRole = 'staff';
              teamId = teamRecs[0].id;
              orgId = teamRecs[0].org_id;
            }
          } catch (err) {
            console.error("Error resolving team token via RPC:", err);
          }
        }
      }

      setMessage({ text: "Connexion réussie ! Chargement...", type: 'success' });
      
      // Perform anonymous sign in to bind RLS policies
      await supabase.auth.signInAnonymously();

      // Update auth user metadata for RLS policies
      await supabase.auth.updateUser({
        data: {
          email: cleanEmail,
          role: cleanEmail === 'benmloukaamen@gmail.com' ? 'superadmin' : userRole,
          staff_id: teamId,
          org_id: orgId
        }
      });

      // Force refresh session to ensure updated metadata claims are present in JWT for subsequent queries/RLS
      await supabase.auth.refreshSession();
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err: any) {
      console.error("Error during OTP login verification:", err);
      setMessage({ text: `Erreur de connexion : ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] w-full items-center justify-center bg-[#0A0B0E] p-4 text-white font-sans relative selection:bg-indigo-500/30">
      {/* Background abstract ambient glows */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.08),transparent_45%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.05),transparent_45%)] pointer-events-none" />

      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#2D3139] bg-[#111318]/90 p-8 shadow-2xl backdrop-blur-xl transition-all duration-300 hover:border-slate-800">
        
        {/* Subtle decorative top border glow */}
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />

        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-indigo-500/10 border border-indigo-500/20 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/5">
            {codeSent ? (
              <ShieldCheck className="w-7 h-7 text-indigo-400 animate-pulse" />
            ) : (
              <Lock className="w-6 h-6 text-indigo-400" />
            )}
          </div>
          <h1 className="text-xl font-bold text-slate-100">
            Portail Bip Teams
          </h1>
          <p className="text-xs text-slate-400 mt-2 text-center leading-relaxed">
            {codeSent 
              ? "Entrez le code à 6 chiffres envoyé sur votre adresse email." 
              : "Connexion sécurisée par e-mail sans mot de passe pour Admins, Managers & Team."
            }
          </p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-xl text-xs font-medium text-center flex items-start space-x-2 border backdrop-blur-sm ${
            message.type === 'success' 
              ? 'bg-emerald-500/5 text-emerald-400 border-emerald-500/20' 
              : 'bg-red-500/5 text-red-400 border-red-500/20 animate-shake'
          }`}>
            {message.type === 'success' && <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            <span>{message.text}</span>
          </div>
        )}

        {!codeSent ? (
          <form onSubmit={handleRequestOtp} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Votre Adresse Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nom@exemple.com"
                  required
                  className="w-full rounded-xl border border-[#2D3139] bg-[#0A0B0E] py-3.5 pl-11 pr-4 text-sm font-medium text-slate-200 placeholder-slate-600 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/15"
                />
                <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50 active:scale-[0.98]"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Se Connecter
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center">
                Code de Confirmation (6 chiffres)
              </label>
              <input
                type="text"
                required
                maxLength={6}
                pattern="[0-9]{6}"
                placeholder="000000"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, ''))}
                className="w-full text-center tracking-[0.5em] text-lg font-bold rounded-xl border border-[#2D3139] bg-[#0A0B0E] py-4 text-slate-200 placeholder-slate-700 transition-all focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/15"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setCodeSent(false)}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-xl border border-[#2D3139] bg-[#14161C] py-3 text-xs font-bold text-slate-400 transition-all hover:bg-[#1C1F27] hover:text-white"
              >
                Retour
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-[2] flex items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 text-xs font-bold uppercase tracking-wider text-white transition-all hover:bg-indigo-500 focus:outline-none disabled:opacity-50 active:scale-[0.98]"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Valider Connexion"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-8 border-t border-[#2D3139]/50 pt-6 text-center">
          <span className="text-[10px] text-slate-500">🔒 Accès Sécurisé Bip Teams & Resend</span>
        </div>

      </div>
    </div>
  );
}
